import { Injectable, ViewContainerRef, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  CommonUtilsService,
  DropdownQuestionV3,
  FileQuestionV3,
  QuestionsV3,
  TextQuestionV3,
  QuestionV3ControlTypes
} from '@bepatient/common';
import {
  CoreActionUpdateUserLanguage,
  CorePageModuleIDs,
  CorePopupService,
  CoreRouterActionNavigateToPageModule,
  CoreRouterNavigationIntent,
  CoreUser,
  selectorCoreHttpPending,
  selectorCoreLogin
} from '@bepatient/core';
import {
  EDocsActionCreateFile,
  EDocsActionCreateFolder,
  EDocsActionDeleteFile,
  EDocsActionDeleteFolder,
  EDocsActionSelectFile,
  EDocsActionSelectFolder,
  EDocsFile,
  EDocsFolder,
  selectorEDocsEDocs,
  EDocsActionDownloadFile
} from '@bepatient/edocs-store';
import { AlertController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { maxSizeValidator } from 'libs/common/src/lib/dynamic-form-v3/validators/dynamic-form-photo.validator';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { EdocsPopoverComponent } from '../components/edocs-popover/edocs-popover.component';

export const enum PopupActions {
  EDIT,
  DELETE,
  DOWNLOAD,
  CREATE_DOC,
  CREATE_FOLDER
}

export interface PopupAction {
  icon: string;
  label: string;
  color?: string;
  action: PopupActions;
  file?: EDocsFile;
  folder?: EDocsFolder;
}

@Injectable()
export class EDocsUtilsService {
  private utils = inject(CommonUtilsService);
  private translate = inject(TranslateService);
  private popupService = inject(CorePopupService);
  private alertController = inject(AlertController);
  private platform = inject(Platform);

  ACTION_EDIT: PopupAction;
  ACTION_DELETE: PopupAction;
  ACTION_DOWNLOAD: PopupAction;
  ACTION_CREATE_DOC: PopupAction;
  ACTION_CREATE_FOLDER: PopupAction;

  private selectedFileSubject = new BehaviorSubject<EDocsFile>(void 0);
  selectedFile$ = this.selectedFileSubject.asObservable();

  private selectedFolderSubject = new BehaviorSubject<EDocsFolder>(void 0);
  selectedFolder$ = this.selectedFolderSubject.asObservable();

  private popupInitiator: EDocsFile | EDocsFolder;

  private instanceColor: string;
  private fileSystemPermission;

  vm$ = combineLatest([
    this.selectedFile$,
    this.selectedFolder$,
    this.utils.instanceColor$,
    this.utils.storeSelect(selectorEDocsEDocs),
    this.utils.storeSelect(selectorCoreHttpPending),
    this.utils.storeSelect(selectorCoreLogin)
  ]).pipe(
    map(([selectedFile, selectedFolder, color, edocs, pending, login]) => ({
      edocs,
      selectedFile,
      selectedFolder,
      color,
      pending,
      login
    })),
    tap(res => {
      this.instanceColor = res.color;
      if (res && res.login && res.login.language) {
        this.utils.storeDispatch(
          new CoreActionUpdateUserLanguage({
            language: res.login.language,
            rightToLeft: res.login.language
              ? res.login.language === 'ar'
              : false
          })
        );
      }
      this.ACTION_EDIT = {
        label: this.translate.instant('EDIT'),
        icon: 'create',
        action: PopupActions.EDIT
      };
      this.ACTION_DELETE = {
        label: this.translate.instant('DELETE'),
        icon: 'trash',
        color: 'danger',
        action: PopupActions.DELETE
      };
      this.ACTION_DOWNLOAD = {
        label: this.translate.instant('DOWNLOAD'),
        icon: 'arrow-down-circle',
        action: PopupActions.DOWNLOAD
      };
      this.ACTION_CREATE_DOC = {
        label: this.translate.instant('EDOCUMENTS.ADD_FILE'),
        icon: 'document',
        action: PopupActions.CREATE_DOC
      };
      this.ACTION_CREATE_FOLDER = {
        label: this.translate.instant('EDOCUMENTS.ADD_FOLDER'),
        icon: 'folder',
        action: PopupActions.CREATE_FOLDER
      };
    })
  );

  setViewContainerRef(viewContainerRef: ViewContainerRef) {
    this.popupService.popupServiceRef = viewContainerRef;
  }

  fileSelect(file: EDocsFile, isInFolder = false): void {
    this.selectedFileSubject.next(file);
    if (!isInFolder) {
      this.selectedFolderSubject.next(void 0);
    }
    this.createPopup(
      [this.ACTION_DOWNLOAD, this.ACTION_EDIT, this.ACTION_DELETE],
      file
    );
    if (this.platform.is('hybrid') && this.platform.is('android')) {
      this.utils.checkFileSystemPermission().then(status => {
        this.fileSystemPermission = status;
      });
    }
  }

  folderSelect(folder: EDocsFolder): void {
    folder.isSelected = !folder.isSelected;
    this.selectedFileSubject.next(void 0);
    this.selectedFolderSubject.next(folder);
  }

  folderPopup(folder: EDocsFolder): void {
    this.selectedFolderSubject.next(folder);
    this.createPopup(
      folder.files
        ? [this.ACTION_EDIT]
        : [this.ACTION_EDIT, this.ACTION_DELETE],
      folder
    );
  }

  createDocumentPopup(): void {
    // Currently only file can be created.
    this.utils.storeDispatch(new EDocsActionCreateFile());
    // TODO: uncomment to show popup to show option to create both file and folders.
    // this.createPopup([this.ACTION_CREATE_DOC, this.ACTION_CREATE_FOLDER]);
  }

  getDocumentForm(
    fileName: string,
    parentFolder: string,
    foldersNames: any[],
    user: CoreUser,
    myFileSetting: any,
    attachmentType: string
  ): {
    currentPage: number;
    questionPages: QuestionsV3[][];
    responses: any;
    readonly: boolean;
  } {
    return {
      currentPage: 0,
      questionPages: this.getDocumentQuestionPages(
        fileName,
        foldersNames,
        user,
        myFileSetting
      ),
      responses: {
        title: fileName,
        attachmentType: attachmentType,
        fileToSave: ''
        // parentFolderUuid: parentFolder,
        // date: new Date().toISOString() //moment().format(user.configuration.dateSettings.dateFormat)
      },
      readonly: false
    };
  }

  getFolderForm(folderName: string): {
    currentPage: number;
    questionPages: QuestionsV3[][];
    responses: any;
    readonly: boolean;
  } {
    return {
      currentPage: 0,
      questionPages: this.getFolderQuestionPages(folderName),
      responses: {
        title: folderName
      },
      readonly: false
    };
  }

  getFileQuestion(form: any): FileQuestionV3 {
    return form.questionPages[0].find(
      question => question.controlType === QuestionV3ControlTypes.FILE
    );
  }

  private getDocumentQuestionPages(
    name: string,
    options: any[],
    user: CoreUser,
    myFileSetting: any
  ): QuestionsV3[][] {
    let formQuestions = [
      new DropdownQuestionV3({
        controlType: 0,
        constraints: [
          {
            type: 'required',
            errorMessage: this.translate.instant('FORM_COMMON.ERROR.REQUIRED')
          }
        ],
        disabled: false,
        display: true,
        key: 'attachmentType',
        order: 1,
        page: 0,
        placeholder: 'EDOCUMENTS.FORM.ATTACHMENT_TYPE.PLACEHOLDER',
        readonly: false,
        required: true,
        customClasses: ['ion-text-start'],
        conditionalRules: { isConditionnal: false },
        label: this.translate.instant('EDOCUMENTS.FORM.ATTACHMENT_TYPE.LABEL'),
        hideEmptyResponse: true,
        options: this.getAttachmentTypes(myFileSetting.attachmentTypes),
        validators: [Validators.required]
      }),
      new FileQuestionV3({
        controlType: 19,
        constraints: [
          {
            type: 'required',
            errorMessage: this.translate.instant('FORM_COMMON.ERROR.REQUIRED')
          },
          {
            type: 'maxSize',
            maxSize: myFileSetting.fileSize,
            errorMessage: this.translate.instant('FORM_COMMON.ERROR.FILESIZE', {
              limit: myFileSetting.fileSize
            })
          }
        ],
        disabled: false,
        display: true,
        key: 'fileToSave',
        order: 1,
        page: 0,
        placeholder: '',
        readonly: false,
        required: true,
        customClasses: [''],
        conditionalRules: { isConditionnal: false },
        label: this.translate.instant('EDOCUMENTS.FORM.DOCUMENT'),
        validators: [
          Validators.required,
          maxSizeValidator(myFileSetting.fileSize)
        ]
      }),
      new TextQuestionV3({
        controlType: 12,
        constraints: [
          {
            type: 'required',
            errorMessage: this.translate.instant('FORM_COMMON.ERROR.REQUIRED')
          },
          {
            type: 'pattern',
            errorMessage: this.translate.instant(
              'FORM_COMMON.ERROR.MYFILE_INVALID_PATTERN'
            )
          }
        ],
        disabled: false,
        display: true,
        key: 'title',
        order: 1,
        page: 0,
        pattern: '^[a-zA-Z0-9 _-]+',
        placeholder: this.translate.instant('EDOCUMENTS.FORM.FILE.PLACEHOLDER'),
        readonly: false,
        required: false,
        customClasses: [''],
        conditionalRules: { isConditionnal: false },
        label: this.translate.instant('EDOCUMENTS.FORM.FILE.LABEL'),
        value: name,
        validators: [Validators.required]
      })
    ];
    // Add Form: Document Type, Document Picker, Document Name
    // Edit Form: Document Type, Document Name
    name === '' ? formQuestions : formQuestions.splice(1, 1);
    return [formQuestions];
  }

  private getFolderQuestionPages(name: string): QuestionsV3[][] {
    return [
      [
        new TextQuestionV3({
          controlType: 0,
          constraints: [{ type: 'required', errorMessage: 'is required' }],
          disabled: false,
          display: true,
          key: 'title',
          order: 1,
          page: 0,
          placeholder: this.translate.instant(
            'EDOCUMENTS.FORM.FOLDER.PLACEHOLDER'
          ),
          readonly: false,
          required: true,
          customClasses: [''],
          conditionalRules: { isConditionnal: false },
          label: this.translate.instant('EDOCUMENTS.FORM.FOLDER.LABEL'),
          value: name,
          validators: [Validators.required]
        })
      ]
    ];
  }

  private createPopup(
    actions: Array<PopupAction>,
    initiator?: EDocsFile | EDocsFolder
  ): void {
    this.popupInitiator = initiator;
    this.popupService
      .create(EdocsPopoverComponent, {
        inputs: {
          actions
        },
        backdropDismiss: true
      })
      .then(component =>
        component.applyAction.subscribe(action => {
          this.applyPopupAction(action);
          this.popupService.dismiss();
        })
      );
  }

  private isFile(doc: EDocsFile | EDocsFolder): doc is EDocsFile {
    return (doc as EDocsFile).date !== void 0;
  }

  private applyPopupAction(action: PopupAction): void {
    switch (action.action) {
      case PopupActions.EDIT:
        this.utils.storeDispatch(
          this.isFile(this.popupInitiator)
            ? new EDocsActionSelectFile(this.popupInitiator)
            : new EDocsActionSelectFolder(this.popupInitiator)
        );
        break;
      case PopupActions.DELETE:
        this.deleteConfirmation().then(
          () =>
            this.utils.storeDispatch(
              this.isFile(this.popupInitiator)
                ? new EDocsActionDeleteFile(this.popupInitiator)
                : new EDocsActionDeleteFolder(this.popupInitiator)
            ),
          () => {}
        );
        break;
      case PopupActions.DOWNLOAD:
        if (this.isFile(this.popupInitiator)) {
          // File System Permission check for android
          if (
            this.fileSystemPermission &&
            this.fileSystemPermission === 'denied'
          ) {
            // Guide user to application settings to change permission
            this.utils.openNativeOSSettingPopup(
              this.translate.instant('PERMISSION_POPUP.TITLE'),
              this.translate.instant('PERMISSION_POPUP.FILE_MEDIA_MESSAGE')
            );
          } else {
            this.utils.storeDispatch(
              new EDocsActionDownloadFile(this.popupInitiator)
            );
          }
        }
        break;
      case PopupActions.CREATE_DOC:
        this.utils.storeDispatch(new EDocsActionCreateFile());
        break;
      case PopupActions.CREATE_FOLDER:
        this.utils.storeDispatch(new EDocsActionCreateFolder());
        break;
    }
  }

  private deleteConfirmation(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.alertController
        .create({
          header: this.translate.instant(
            'EDOCUMENTS.DOCUMENT.CONFIRM_ALERT.TITLE'
          ),
          message: this.translate.instant(
            'EDOCUMENTS.DOCUMENT.CONFIRM_ALERT.MESSAGE',
            { filename: this.popupInitiator.title }
          ),
          cssClass: 'bepatient-shared-alert',
          mode: 'ios',
          buttons: [
            {
              text: this.translate.instant('MY_FORM.ALERT_ACTION_BACK.CANCEL'),
              role: 'cancel',
              cssClass: ['default']
            },
            {
              text: this.translate.instant('MY_FORM.ALERT_ACTION_BACK.OK'),
              role: 'ok',
              cssClass: ['bepatient-shared-alert-last-button']
            }
          ]
        })
        .then(alert => {
          alert.present();
          alert.onDidDismiss().then(response => {
            return response.role === 'ok' ? resolve() : reject();
          });
          if (this.instanceColor) {
            alert.style.setProperty(
              '--bepatient-shared-alert-color',
              this.instanceColor
            );
          }
        });
    });
  }

  private getAttachmentTypes(attachmentTypes: any[]): Array<any> {
    return (attachmentTypes || []).map(attachType => ({
      value: attachType.attachment_type,
      key: attachType.attachment_label
    }));
  }

  openMenu() {
    this.utils.storeDispatch(
      new CoreRouterActionNavigateToPageModule({
        id: CorePageModuleIDs.MENU,
        pageIndex: 0,
        intent: CoreRouterNavigationIntent.FORWARD
      })
    );
  }
}
